/**
 * Display helpers used across the dashboard. Keeping formatting in one place
 * makes it easier to keep the UI internally consistent.
 */

import { ethers } from "ethers";
import { chainNowMs } from "./chainClock.js";

const USDC_DECIMALS = 6;

// Turbo's advertised APR is a DISPLAY-ONLY marketing label — interest
// actually accrues at the real stored rate (20%, same as Standard). This
// fixed percentage is shown wherever a Turbo deposit's APR % appears in
// the UI; it must NEVER be fed into an interest/maturity calculation
// (those read the real `annualRateBps`).
export const TURBO_DISPLAY_APR_PCT = 27.5;

/**
 * The APR percentage to SHOW for a deposit. Turbo deposits (productMode
 * 1) render the cosmetic 27.5% label; everything else shows the real
 * stored rate. Use this ONLY for the displayed % — never for math.
 */
export function displayAprPct(deposit, fallbackBps = 2000) {
    if (Number(deposit?.productMode) === 1) return TURBO_DISPLAY_APR_PCT;
    return (Number(deposit?.annualRateBps) || fallbackBps) / 100;
}

/**
 * The APR to SHOW as a human label, with Turbo's leveraged headline
 * qualified as "up to". The full 27.5% only lands for larger deposits
 * (~$10k+); below that the $100 min-deposit loop floor truncates the
 * leverage tail, so the net APR is lower. Per client (Jun 2026): never
 * render a bare Turbo "27.5%" — always "up to 27.5%". Returns a string
 * WITHOUT the trailing "%" so callers can append it (e.g. "up to 27.5",
 * "20"). Display-only — never use for interest/maturity math.
 */
export function displayAprLabel(deposit, fallbackBps = 2000) {
    if (Number(deposit?.productMode) === 1) return `up to ${TURBO_DISPLAY_APR_PCT}`;
    return String((Number(deposit?.annualRateBps) || fallbackBps) / 100);
}

/**
 * Accrued interest for ONE deposit, computed EXACTLY the way the
 * contract does it — `GilderSystem.calculateAccruedInterest`:
 *
 *     elapsedDays = (min(now, maturity) - startTime) / 1 day   // integer
 *     interest    = principal * annualRateBps * elapsedDays
 *                   ──────────────────────────────────────
 *                            10000 * 365
 *
 * The contract credits interest per WHOLE elapsed DAY (spec §4.2,
 * "Daily Interest"), so a deposit younger than 24h has 0 accrued
 * interest — and an early exit / settlement pays 0 interest on it.
 * The UI MUST use this same day-stepped figure, never a per-second
 * one: showing per-second interest the contract never credits is what
 * made a 2-hour deposit display "+$0.0047" yet pay back exactly $80.
 *
 * All amounts are BigInt USDC base units; times are unix seconds.
 */
export function liveAccruedInterest(
    principal,
    startTime,
    maturityTime,
    nowSec,
    annualRateBps = 2000,
    realizedInterest = 0
) {
    const p = BigInt(principal || 0);
    const start = BigInt(startTime || 0);
    const maturity = BigInt(maturityTime || 0);
    const now = BigInt(nowSec);
    const end = now < maturity ? now : maturity;
    if (end <= start) return 0n;
    const elapsedDays = (end - start) / 86400n; // integer days, like the contract
    const total = (p * BigInt(annualRateBps) * elapsedDays) / (10000n * 365n);
    // Subtract any interest already realized via auto-compound. The §4.2
    // yield formula is cumulative-from-startTime, so without this net-out
    // the UI would keep showing the full $167.49 after a successful
    // compound — the symptom the user reported with deposit #5. Callers
    // who don't track realization (legacy paths) pass 0 / omit the arg
    // and behave as before.
    const realized = BigInt(realizedInterest || 0);
    return total > realized ? total - realized : 0n;
}

/**
 * DISPLAY-ONLY per-second smoother for deposit accrued interest. The
 * Dashboard / Sidebar headline cards refresh every second; using the
 * day-stepped `liveAccruedInterest` figure leaves them visually frozen
 * until the integer-day boundary flips. That's correct for settlement
 * / early-exit math — the contract credits zero below 24h and steps
 * per whole day — but it makes the headline numbers look stale.
 *
 * This helper returns a smoothly interpolated per-second figure for
 * the same formula:
 *
 *     interest = principal * annualRateBps * elapsedSec
 *                ──────────────────────────────────────
 *                       10000 * 365 * 86400
 *
 * Rules:
 *   - Accrues from the FIRST SECOND. Per client (Jun 2026), the headline
 *     cards must visibly accumulate from minute one so a new depositor can
 *     see the position working, "even if it's only cents". This means the
 *     display leads the contract's day-stepped, sub-24h-zero settlement
 *     figure by up to ~1 day's accrual (at $10k @ 20% APR, ~$5.48 max) —
 *     an accepted trade-off for a live-motion headline. Settlement / exit /
 *     liquidation paths MUST keep using day-stepped `liveAccruedInterest`,
 *     so the actual payout is never overstated.
 *   - Caps at `maturityTime`. Past maturity the contract stops
 *     accruing, and the display follows.
 *   - Nets out `realizedInterest` (auto-compound consumed).
 */
export function liveAccruedInterestSmooth(
    principal,
    startTime,
    maturityTime,
    nowSec,
    annualRateBps = 2000,
    realizedInterest = 0
) {
    const p = BigInt(principal || 0);
    const start = BigInt(startTime || 0);
    const maturity = BigInt(maturityTime || 0);
    const now = BigInt(nowSec);
    const end = now < maturity ? now : maturity;
    if (end <= start) return 0n;
    const elapsedSec = end - start;
    const total =
        (p * BigInt(annualRateBps) * elapsedSec) / (10000n * 365n * 86400n);
    const realized = BigInt(realizedInterest || 0);
    return total > realized ? total - realized : 0n;
}

/**
 * Live accrued interest for ONE loan — mirrors the per-SECOND formula
 * the Lending contract uses in `_accrueInterest`:
 *
 *     pending = principal * aprBps * elapsedSec
 *              ──────────────────────────────────
 *                        10000 * secondsPerYear
 *
 * Unlike the deposit yield (which steps per WHOLE DAY — spec §4.2),
 * loan interest accrues per second because the borrower pays interest
 * on capital they actually hold for any duration — otherwise the first
 * 24 h would be free and the contract would lose money on same-day
 * borrow-and-repay loops.
 *
 * `storedAccruedInterest` is the figure frozen at the last on-chain
 * event (draw / repay / liquidation); `pending` is the per-second
 * extrapolation since `lastAccruedAtMs`. Their sum is the live debt
 * delta the user would pay if they called repay this instant.
 */
export function liveLoanInterest(loan, nowMs = chainNowMs()) {
    if (!loan) return 0n;
    const principal = BigInt(loan.principal || 0);
    const stored = BigInt(loan.storedAccruedInterest || 0);
    // Loan interest can never tick with zero principal — there's no
    // debt to accrue against. Early return preserved as before.
    if (principal === 0n) return stored;
    // Default APR when the loan record was indexed before aprBps was
    // populated. 1500 bps (15%) matches the protocol's Lending
    // module default — see config.protocol.loanAprBps.
    const aprBps = BigInt(loan.aprBps || 1500);
    // §V6.2 — when `lastAccruedAt` is missing (CYR-opened loans whose
    // initial LoanStateUpdated event hasn't been indexed yet, OR
    // fresh-tx state during the backend's 15s gap), fall back to
    // `openedAt`, and as last resort the loan record's `createdAt`.
    // Without this the per-second projection short-circuits to
    // `stored` and the page's headline / row Interest column stays
    // frozen at $0 even while chain is happily accruing. Using "now"
    // would make elapsedSec=0 (still no tick), so we deliberately
    // back off by a small fixed window to seed visible motion until
    // the backend supplies a real timestamp.
    const lastMs = Number(
        loan.lastAccruedAt ||
        loan.openedAt ||
        (loan.createdAt ? new Date(loan.createdAt).getTime() : 0) ||
        (nowMs - 60_000) // worst-case: 1 minute ago, so tick starts immediately
    );
    // `nowMs` is CHAIN time (default = chainNowMs() from the app's single
    // chain clock; callers pass the same). `lastMs` (lastAccruedAt) is an
    // on-chain block timestamp, so both ends are measured by the same
    // clock — essential under time-warp testing where chain time runs
    // weeks/months ahead of the browser wall clock. Mixing the two was
    // the #5-vs-#6 divergence (one loan frozen, one ticking).
    const elapsedSec = BigInt(Math.max(0, Math.floor((nowMs - lastMs) / 1000)));
    if (elapsedSec === 0n) return stored;
    const pending = (principal * aprBps * elapsedSec) / (10000n * 31_536_000n);
    return stored + pending;
}

/**
 * Decimal-precision rule shared by both USDC formatters:
 *
 *   - An explicit `fractionDigits` pins both bounds to a fixed width.
 *   - "Big" amount (value ≥ 100 — the digits before the dot dominate) →
 *     exactly 2 decimals, TRUNCATED, never rounded. We drop the trailing
 *     4 base-unit digits on the BigInt itself, so 11,609.448726 shows as
 *     "11,609.44" (never "11,609.45") with no float drift.
 *   - "Small" amount (value < 100) → 2–6 decimals, so a tiny non-zero
 *     amount keeps its real digits ("0.000123", not "0.00"), a value
 *     like 1.589 is shown exactly, and a true zero shows "0.00".
 *
 * @param {bigint} raw   amount in USDC base units
 * @param {number} value the same amount as a JS number
 */
function formatUsdcDecimals(raw, value, fractionDigits) {
    if (fractionDigits !== undefined) {
        return value.toLocaleString(undefined, {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits
        });
    }
    if (value >= 100 || value <= -100) {
        // Truncate to 2 dp by zeroing the last 4 base-unit digits — exact
        // integer math, so no rounding and no floating-point drift.
        const truncated = (raw / 10_000n) * 10_000n;
        const v2 = Number(ethers.formatUnits(truncated, USDC_DECIMALS));
        return v2.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }
    return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6
    });
}

/** Format a USDC base-unit amount as a human readable USD string. */
export function formatUsdc(amount, fractionDigits) {
    if (amount === undefined || amount === null) return "0.00";
    const raw = BigInt(amount.toString());
    const value = Number(ethers.formatUnits(raw, USDC_DECIMALS));
    return formatUsdcDecimals(raw, value, fractionDigits);
}

/**
 * Compact USDC formatter. Below 100,000 (1 lakh) the value is shown with
 * grouping commas and the shared `formatUsdcDecimals` precision rule
 * (big → 2 dp truncated, small → up to 6 dp). From 100,000 up it is
 * rendered with a K/M suffix:
 *   100,000      → "100K"
 *   999,000      → "999K"
 *   1,200,000    → "1.2M"
 *   10,000,000   → "10M"
 *   1,000,000,000 → "1000M"
 * Pass an explicit `fractionDigits` to pin both decimal bounds.
 */
export function formatUsdcCompact(amount, fractionDigits) {
    if (amount === undefined || amount === null) return "0.00";
    const raw = BigInt(amount.toString());
    const value = Number(ethers.formatUnits(raw, USDC_DECIMALS));
    if (value < 100000) {
        return formatUsdcDecimals(raw, value, fractionDigits);
    }
    if (value < 1_000_000) {
        return `${trim1(value / 1000)}K`;
    }
    return `${trim1(value / 1_000_000)}M`;
}

function trim1(n) {
    const rounded = Math.round(n * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

const GIL_DECIMALS = 18;

/**
 * Compact formatter for GIL — the protocol token, which has 18 decimals
 * (NOT 6 like USDC). Using `formatUsdcCompact` on a GIL amount renders a
 * nonsense figure 10^12 too large, so GIL must always go through here.
 *   100,000 GIL  → "100K"
 *   1,000,000    → "1M"
 *   12,345       → "12,345"
 */
export function formatGilCompact(amount) {
    if (amount === undefined || amount === null) return "0";
    const value = Number(ethers.formatUnits(amount.toString(), GIL_DECIMALS));
    // Compact suffixes up to Trillion — GIL has a 1e12 supply, so K/M alone
    // would render supply-scale figures as an unreadable "1000000M".
    if (value < 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    if (value < 1_000_000) return `${trim1(value / 1000)}K`;
    if (value < 1_000_000_000) return `${trim1(value / 1_000_000)}M`;
    if (value < 1_000_000_000_000) return `${trim1(value / 1_000_000_000)}B`;
    return `${trim1(value / 1_000_000_000_000)}T`;
}

/**
 * Computes the realized-yield delta string used on the dashboard stat
 * cards (e.g. "+0.5%"). Defined as `accruedInterest / principal * 100`,
 * rounded to one decimal. Falls back to "+0.0%" when there's no
 * principal so the cards never show NaN.
 *
 * Both inputs are BigInts (USDC base units). We multiply by 1000 before
 * dividing so the BigInt division preserves one decimal of precision.
 */
export function formatYieldDelta(principalBigInt, interestBigInt) {
    const principal = BigInt(principalBigInt || 0n);
    const interest = BigInt(interestBigInt || 0n);
    if (principal === 0n) return "+0.0%";
    const tenths = (interest * 1000n) / principal;
    const value = Number(tenths) / 10;
    return `+${value.toFixed(1)}%`;
}

// Trailing-window constants for the 24h stat-card deltas.
const SECS_PER_DAY = 86400n;
const SECS_PER_YEAR = 31536000n; // 365 * 86400

/**
 * Interest a single active term deposit accrued over the trailing 24h.
 *
 * Term deposits earn a fixed APY, so the daily accrual is fully
 * deterministic — no historical snapshot is needed:
 *   principal * annualRateBps / 10000 * windowSecs / secondsPerYear
 *
 * `windowSecs` is capped at the deposit's age, so a position younger
 * than a day only counts the hours it has actually existed (otherwise
 * a brand-new deposit would over-report a full day of yield).
 *
 * All BigInt math (USDC base units) to avoid float drift on large
 * principals.
 */
export function accrued24h(principalBase, startTimeSecs, nowSecs, annualRateBps) {
    const principal = BigInt(principalBase || 0n);
    const ageSecs = Math.max(0, Number(nowSecs) - Number(startTimeSecs || 0));
    const windowSecs = BigInt(Math.min(ageSecs, Number(SECS_PER_DAY)));
    if (principal === 0n || windowSecs === 0n) return 0n;
    return (principal * BigInt(annualRateBps) * windowSecs) / (10000n * SECS_PER_YEAR);
}

/**
 * Formats a trailing-24h percentage delta from the current value and
 * the value 24h ago (both BigInt base units). Suffixed with " · 24h" so
 * the card states its comparison window explicitly. When there is no
 * prior value to compare against (a position opened within the window)
 * it reports "New · 24h" rather than a divide-by-zero percentage.
 */
export function formatDayDelta(nowBig, agoBig) {
    const now = BigInt(nowBig || 0n);
    const ago = BigInt(agoBig || 0n);
    // No real baseline 24h ago — or one so small the "delta" would be a
    // meaningless multi-thousand-percent number (every position in the
    // portfolio is younger than the window). Report it as New rather
    // than a divide-by-near-zero blow-up.
    if (ago <= 0n || now > ago * 11n) {
        return now > 0n ? "New · 24h" : "+0.0% · 24h";
    }
    const tenths = ((now - ago) * 1000n) / ago;
    const value = Number(tenths) / 10;
    return `${value >= 0 ? "+" : ""}${value.toFixed(1)}% · 24h`;
}

/** Parse a user input USDC amount into base units. */
export function parseUsdc(text) {
    if (!text) return 0n;
    return ethers.parseUnits(String(text), USDC_DECIMALS);
}

/** Convert a basis points value (1 bp = 0.01%) to a percentage string. */
export function formatBps(bps, fractionDigits = 2) {
    return `${(Number(bps) / 100).toFixed(fractionDigits)}%`;
}

/** Truncate an Ethereum address for display. */
export function shortAddress(address) {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/** Convert seconds remaining to a "Xy Ym Zd" countdown style string. */
export function formatCountdown(secondsRemaining) {
    if (secondsRemaining <= 0) return "Matured";
    const days = Math.floor(secondsRemaining / 86400);
    const hours = Math.floor((secondsRemaining % 86400) / 3600);
    return `${days}d ${hours}h`;
}

const STATE_LABELS = ["None", "Active", "Matured", "Dormant", "Abandoned", "Exited", "Closed"];

/** Map the contract state enum to a human readable label. */
export function depositStateLabel(state) {
    return STATE_LABELS[Number(state) || 0] || "Unknown";
}
